import React, { useState, useEffect } from 'react';
import {
    Box, Typography, FormControl, InputLabel, Select, MenuItem,
    Button, Accordion, AccordionSummary, AccordionDetails, Chip,
    Radio, RadioGroup, FormControlLabel, CircularProgress, Alert,
    TextField, Divider, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MergeIcon from '@mui/icons-material/MergeType';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close'; // Icon for removing items
import { useAuth } from '../AuthProvider';
import { getApiToken } from '../authUtils';
import { useMsal } from '@azure/msal-react';
const urlconfig = require('../config/urlconfig');

const NameNormalization = () => {
    const { account } = useAuth();
    const { instance } = useMsal();
    const [type, setType] = useState('CLIENT');
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchCandidates = async () => {
        setLoading(true);
        setMessage(null);
        try {
            const token = await getApiToken(instance, account);
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/admin/normalization-candidates?type=${type}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await response.json();
            setCandidates(data);
        } catch (error) {
            console.error(error);
            setMessage({ type: 'error', text: 'Failed to fetch candidates.' });
        } finally {
            setLoading(false);
        }
    };

    // UPDATED: Now accepts 'activeVariations' list from the child component
    const handleMerge = async (group, finalTargetName, activeVariations) => {
        if (!finalTargetName || !finalTargetName.trim()) {
            alert("Please enter a valid name.");
            return;
        }

        if (!activeVariations || activeVariations.length === 0) {
            alert("No items selected to merge.");
            return;
        }

        const token = await getApiToken(instance, account);

        try {
            const response = await fetch(`${urlconfig.backend_api_base_url}/api/v2/admin/merge-names`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    type: type,
                    targetName: finalTargetName.trim(),
                    sourceNames: activeVariations // Send only the non-removed items
                })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: `Successfully merged ${activeVariations.length} records into "${finalTargetName}"` });
                setCandidates(prev => prev.filter(c => c !== group));
            } else {
                setMessage({ type: 'error', text: 'Merge failed on server.' });
            }
        } catch (e) {
            setMessage({ type: 'error', text: 'Merge failed due to network error.' });
        }
    };

    const filteredCandidates = candidates.filter(group => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const primaryMatch = group.primary.toLowerCase().includes(query);
        const variationMatch = group.variations.some(v => v.toLowerCase().includes(query));
        return primaryMatch || variationMatch;
    });

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Name Normalization Tool</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Identify and merge similar names (e.g., "Envent", "Envent Ltd") into a single standardized name for better analytics.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Type</InputLabel>
                    <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                        <MenuItem value="CLIENT">Client Names</MenuItem>
                        <MenuItem value="VENDOR">Vendor Names</MenuItem>
                    </Select>
                </FormControl>

                <Button variant="contained" onClick={fetchCandidates} disabled={loading} startIcon={<SearchIcon />}>
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'Scan for Duplicates'}
                </Button>

                {candidates.length > 0 && (
                    <TextField
                        size="small"
                        placeholder="Search names..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{ ml: 'auto', minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                )}
            </Box>

            {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

            {candidates.length === 0 && !loading && (
                <Typography color="text.secondary" sx={{ mt: 2, fontStyle: 'italic' }}>
                    Click "Scan" to find duplicate names.
                </Typography>
            )}

            {candidates.length > 0 && filteredCandidates.length === 0 && (
                <Typography color="text.secondary" sx={{ mt: 2 }}>
                    No results found for "{searchQuery}".
                </Typography>
            )}

            <Box sx={{ mt: 2 }}>
                {filteredCandidates.map((group, index) => (
                    <NormalizationGroup
                        key={index}
                        group={group}
                        onMerge={handleMerge}
                    />
                ))}
            </Box>
        </Box>
    );
};

// Helper Component for each Group
const NormalizationGroup = ({ group, onMerge }) => {
    const [selected, setSelected] = useState(group.primary);
    const [customName, setCustomName] = useState('');

    // 1. Maintain local state of variations so we can remove items
    const [localVariations, setLocalVariations] = useState(group.variations);

    const handleMergeClick = () => {
        const finalName = selected === '__custom__' ? customName : selected;
        // 2. Pass localVariations to the parent merge function
        onMerge(group, finalName, localVariations);
    };

    // 3. Handler to remove an item from the list
    const handleRemoveVariation = (variationToRemove) => {
        setLocalVariations(prev => prev.filter(v => v !== variationToRemove));
    };

    // If all items are removed, don't show the group (or show a message)
    if (localVariations.length <= 1) {
       // Optional: You could return null here to hide empty groups,
       // but keeping it allows the user to see they cleared it or merge single items if needed.
    }

    return (
        <Accordion defaultExpanded sx={{ mb: 1, border: '1px solid #eee' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#f9f9f9' }}>
                <Typography sx={{ width: '33%', flexShrink: 0, fontWeight: 'bold' }}>
                    {localVariations.length} Variations Found
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                    Suggested: {group.primary}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Choose the standard name to use:
                </Typography>

                <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
                    {localVariations.map((variant) => (
                        <Box key={variant} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1, '&:hover .remove-btn': { opacity: 1 } }}>
                            <FormControlLabel
                                value={variant}
                                control={<Radio size="small" />}
                                label={
                                    <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {variant}
                                        {variant === group.primary && <Chip label="Suggested" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                                    </Box>
                                }
                            />
                            {/* 4. Remove Button (Visible on Hover) */}
                            <Tooltip title="Remove from group (do not merge)">
                                <IconButton
                                    size="small"
                                    className="remove-btn"
                                    onClick={() => handleRemoveVariation(variant)}
                                    sx={{ opacity: 0.2, transition: 'opacity 0.2s', color: 'text.disabled', '&:hover': { color: 'error.main', opacity: 1 } }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    ))}

                    <Divider sx={{ my: 1 }} />

                    <FormControlLabel
                        value="__custom__"
                        control={<Radio size="small" color="secondary" />}
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <EditIcon fontSize="small" color="action" />
                                <Typography variant="body2">Set Manual Name</Typography>
                            </Box>
                        }
                    />
                </RadioGroup>

                {selected === '__custom__' && (
                    <Box sx={{ ml: 4, mt: 1, mb: 2, maxWidth: 400 }}>
                        <TextField
                            size="small"
                            fullWidth
                            label="Type Standard Name Here"
                            placeholder="e.g. Envent Worldwide Pvt Ltd"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            autoFocus
                        />
                    </Box>
                )}

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color={selected === '__custom__' ? "secondary" : "warning"}
                        startIcon={<MergeIcon />}
                        onClick={handleMergeClick}
                        disabled={localVariations.length === 0 || (selected === '__custom__' && !customName.trim())}
                    >
                        Merge {localVariations.length} items into "{selected === '__custom__' ? (customName || '...') : selected}"
                    </Button>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default NameNormalization;